import React, { useState, useEffect, useCallback } from 'react'
import { Grid, Typography, Divider, CircularProgress } from '@material-ui/core'
import { Page, Header, CustomButton, ModalConfirmacao, SecaoGenerica } from 'components'
import constants from 'utils/constants'
import useRouter from 'utils/useRouter'
import { useFormState } from 'hooks/useFormState'
import { useVestigioService } from 'hooks/useVestigioService'
import { useSetorService } from 'hooks/useSetorService'
import { useAuth } from 'hooks/useAuth'
import { useUsuarioService } from 'hooks/useUsuarioService'
import { cutMaxLength } from 'utils/helpers'
import useStyles from './styles'
import { useParams } from 'react-router'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import CheckIcon from '@material-ui/icons/Check'
import { FormLacreRetorno } from './components/FormLacreRetorno'
import { FormEntregaRetorno } from './components/FormEntregaRetorno'

const schema = {
  lacre: {
    presence: { allowEmpty: false, message: '^Lacre é obrigatório' },
  },
  setorDestino: {
    presence: { allowEmpty: false, message: '^Setor destino é obrigatório' },
  },
  usuarioRecebedor: {
    presence: { allowEmpty: false, message: '^Usuário recebedor é obrigatório' },
  },
}

const maxLength250 = (newValue) => cutMaxLength(newValue, 250)

export const RetornarVestigio = (props) => {
  const { route } = props
  const classes = useStyles()
  const { currentUser, verificarPermissao } = useAuth()
  const vestigioService = useVestigioService()
  const setorService = useSetorService()
  const usuarioService = useUsuarioService()
  const params = useParams()
  const router = useRouter()
  const [vestigio, setVestigio] = useState(null)
  const [processandoVestigio, setProcessandoVestigio] = useState(false)
  const [setores, setSetores] = useState([]) //eslint-disable-line
  const [processandoSetores, setProcessandoSetores] = useState(false)
  const [usuarios, setUsuarios] = useState([]) //eslint-disable-line
  const [processandoUsuarios, setProcessandoUsuarios] = useState(false)
  const [abrirEspelho, setAbrirEspelho] = useState(false)
  const [isFormError, setIsFormError] = useState(false)
  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const retornoVestigioForm = useFormState(schema, {
    lacre: null,
    tipoEmbalagem: null,
    outroTipoEmbalagem: null,
    corEmbalagem: null,
    outraCorEmbalagem: null,
    tipoFecho: null,
    outroTipoFecho: null,
    logomarca: null,
    tipoLogomarca: null,
    setorDestino: null,
    usuOrig: null,
    usuarioOrigemInput: null,
    setDest: null,
    arquivos: [],
  })

  const fetchSetores = useCallback(async () => {
    const result = await setorService.listarSetoresAsync(setProcessandoSetores)
    setSetores(result)
  }, [])

  const fetchUsuarios = useCallback(async () => {
    const result = await usuarioService.listarUsuariosAsync(setProcessandoUsuarios)
    setUsuarios(result)
  }, [])

  const fetchVestigio = useCallback(async () => {
    const response = await vestigioService.listarVestigioAsync(params.id, setProcessandoVestigio)
    setVestigio(response)
  }, [params.id])

  const handleClickConfirmar = useCallback(() => {
    setAbrirEspelho(true)
  }, [])

  const handleConfirmarOperacao = useCallback(async () => {
    if (verificarPermissao([constants.PERMISSOES.RETORNAR_VESTIGIO_ADMINISTRADOR])) {
      await vestigioService.retornarVestigioAdministrador(vestigio.id, retornoVestigioForm.formState.values, setProcessandoOperacao)
    } else {
      await vestigioService.retornarVestigio(vestigio.id, retornoVestigioForm.formState.values, setProcessandoOperacao)
    }
    router.history.push(`/casos/caso/vestigio/detalhar/${vestigio.id}`)
  }, [retornoVestigioForm, params.id, vestigio])

  useEffect(() => {
    fetchUsuarios()
  }, [fetchUsuarios])

  useEffect(() => {
    fetchSetores()
  }, [fetchSetores])

  useEffect(() => {
    fetchVestigio()
  }, [fetchVestigio])

  useEffect(() => {
    if (vestigio?.id) {
      retornoVestigioForm.setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          lacre: vestigio.lacre,
          tipoEmbalagem: vestigio.tipoEmbalagem,
          outroTipoEmbalagem: vestigio.outroTipoEmbalagem,
          corEmbalagem: vestigio.corEmbalagem,
          outraCorEmbalagem: vestigio.outraCorEmbalagem,
          tipoFecho: vestigio.tipoFecho,
          outroTipoFecho: vestigio.outroTipoFecho,
          logomarca: vestigio.logomarca ? 'SIM' : 'NAO',
          tipoLogomarca: vestigio.tipoLogomarca,
          usuarioRecebedorInput: vestigio.usuarioRecebedorInput,
        },
      }))
    }
  }, [vestigio])

  useEffect(() => {
    if (
      !retornoVestigioForm.formState.values.lacre ||
      !retornoVestigioForm.formState.values.setOrig ||
      !retornoVestigioForm.formState.values.usuOrig ||
      !retornoVestigioForm.formState.values.setDest ||
      (verificarPermissao([constants.PERMISSOES.RETORNAR_VESTIGIO_ADMINISTRADOR]) && !retornoVestigioForm.formState.values.usuDest)
    ) {
      setIsFormError(true)
      return
    }
    setIsFormError(false)
  }, [retornoVestigioForm])

  const handleChangeUsuarioDestino = useCallback((value) => {
    retornoVestigioForm.setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        usuDest: value,
        setDest: null,
      },
    }))
  }, [])

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} pathname={router.location.pathname} router={router} />
      <div className={classes.results}>
        {processandoVestigio || processandoSetores || processandoUsuarios ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : (
          <Grid container>
            <FormLacreRetorno form={retornoVestigioForm} maxLength250={maxLength250} />

            <FormEntregaRetorno
              form={retornoVestigioForm}
              setores={setores}
              usuarios={usuarios}
              verificarPermissao={verificarPermissao}
              currentUser={currentUser}
              handleChangeUsuarioDestino={handleChangeUsuarioDestino}
            />

            <Grid item md={12} sm={12} xs={12}>
              <Divider style={{ margin: '32px 0 16px 0' }} />
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                <CustomButton
                  variant="outlined"
                  colorType="primary"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => router.history.push(`/casos/caso/vestigio/detalhar/${vestigio?.id}`)}
                >
                  Voltar
                </CustomButton>
                <CustomButton
                  variant="contained"
                  colorType="secondary"
                  startIcon={<CheckIcon />}
                  disabled={isFormError || processandoOperacao}
                  isLoading={processandoOperacao}
                  onClick={() => handleClickConfirmar()}
                >
                  Confirmar
                </CustomButton>
              </div>
            </Grid>
          </Grid>
        )}
      </div>

      <ModalConfirmacao
        aberto={abrirEspelho}
        onClose={() => setAbrirEspelho(false)}
        onConfirm={handleConfirmarOperacao}
        processando={processandoOperacao}
        titulo="CONFIRMAÇÃO"
        maxWidth="md"
      >

        <Grid container spacing={2}>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2">
              <b>NÚMERO DO LACRE</b>
            </Typography>
            <Typography variant="body2">{retornoVestigioForm.formState.values.lacre ? retornoVestigioForm.formState.values.lacre : 'Não informado'}</Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2">
              <b>EMBALAGEM</b>
            </Typography>
            <Typography variant="body2">
              TIPO: {retornoVestigioForm.formState.values.tipoEmbalagem || 'NÃO INFORMADO'}. COR: {retornoVestigioForm.formState.values.corEmbalagem || 'NÃO INFORMADO'}. TIPO DE FECHO: {retornoVestigioForm.formState.values.tipoFecho || 'NÃO INFORMADO'}. LOGOMARCA: {retornoVestigioForm.formState.values.logomarca || 'NÃO INFORMADO'}
            </Typography>
          </Grid>

        </Grid>

        <Grid container spacing={2}>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2">
              <b>SETOR DESTINO</b>
            </Typography>
            <Typography variant="body2">{retornoVestigioForm.formState.values.setDest ? retornoVestigioForm.formState.values.setDest.nome?.toUpperCase() : 'Não informado'}</Typography>
          </Grid>
          <Grid item md={6} sm={6} xs={12}>
            <Typography variant="body2">
              <b>USUÁRIO RESPONSÁVEL PELA ENTREGA</b>
            </Typography>
            <Typography variant="body2">
              {retornoVestigioForm.formState.values.usuOrig ? `${retornoVestigioForm.formState.values.usuOrig.nome?.toUpperCase() === 'OUTRO' ? retornoVestigioForm.formState.values.usuarioOrigemInput : retornoVestigioForm.formState.values.usuOrig.nome.toUpperCase()}` : 'NÃO INFORMADO'}
            </Typography>
          </Grid>
        </Grid>
      </ModalConfirmacao>
    </Page>
  )
}
